// src/render/sectionContent.ts
import { createSectionContainer } from "./container";
import { renderSectionContentLines } from "./contentLines";
import type { SectionAttrs } from "../types";

type PaddingPair = { paddingV: string; paddingH: string };

const DEFAULTS = {
  overlay: { v: "3rem", h: "4rem" },
  sideSplit: { v: "2rem", h: "2rem" },
  normal: { v: "2rem", h: "2rem" },
} as const;

// --- helpers ---------------------------------------------------------------

// parse padding keywords (sm/md/lg/tight/loose) or raw value
function parsePadding(val?: string): string {
  if (!val) return "";
  const raw = String(val).trim().toLowerCase();
  if (raw === "tight" || raw === "sm") return "1rem";
  if (raw === "md") return "2rem";
  if (raw === "loose" || raw === "lg") return "4rem";
  // bare numbers → rems
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}rem`;
  return val; // e.g. "32px", "3rem", "5vh", "1svh"
}

function parsePair(val?: string): { v?: string; h?: string } {
  if (!val) return {};
  const parts = String(val).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) {
    const p = parsePadding(parts[0]);
    return { v: p, h: p };
  }
  const v = parsePadding(parts[0]);
  const h = parsePadding(parts[1]);
  return { v, h };
}

// Normalize `Xvh` → `Xsvh` or `Xdvh` to respect mobile browser chrome.
function normalizeVHUnits(val: string, prefer: "svh" | "dvh" = "svh"): string {
  if (!val) return val;
  return String(val).replace(/\b(\d*\.?\d+)vh\b/gi, `$1${prefer}`);
}

// Determines the current orientation, preferring attrs, then window/media.
function getOrientation(attrs: any): "landscape" | "portrait" {
  const raw =
    (attrs as any)["orientation"] ??
    (attrs as any)["screen-orientation"] ??
    (attrs as any)["data-orientation"] ??
    "";
  const t = String(raw).toLowerCase();
  if (t === "landscape" || t === "portrait") return t as any;
  try {
    if (typeof window !== "undefined") {
      if (window.matchMedia?.("(orientation: portrait)")?.matches) return "portrait";
      const w = (window as any).innerWidth, h = (window as any).innerHeight;
      if (Number.isFinite(w) && Number.isFinite(h)) return w >= h ? "landscape" : "portrait";
    }
  } catch {}
  return "landscape";
}

// --- core padding resolver -------------------------------------------------
function resolvePadding(
  attrs: SectionAttrs,
  ctx: { isOverlay: boolean; isSideSplit: boolean }
): PaddingPair {
  const ori = getOrientation(attrs);
  const isPortrait = ori === "portrait";

  // Orientation-specific authored pair (e.g. "12vh 2rem")
  const authoredPair = isPortrait
    ? parsePair((attrs as any)["padding-portrait"])
    : parsePair((attrs as any)["padding-landscape"]);

  // Legacy single-axis and shared shorthands
  const singleV = parsePadding((attrs as any)["padding-v"] || (attrs as any)["padding"]);
  const singleH = parsePadding((attrs as any)["padding-h"] || (attrs as any)["padding"]);
  const both    = parsePair((attrs as any)["padding"]);

  const base = ctx.isOverlay
    ? DEFAULTS.overlay
    : ctx.isSideSplit
    ? DEFAULTS.sideSplit
    : DEFAULTS.normal;

  let paddingV = authoredPair.v || singleV || both.v || base.v;
  let paddingH = authoredPair.h || singleH || both.h || base.h;

  // In portrait, convert any `vh` to `svh` for visible viewport correctness.
  if (isPortrait) {
    paddingV = normalizeVHUnits(paddingV, "svh");
    paddingH = normalizeVHUnits(paddingH, "svh");
  }

  return { paddingV, paddingH };
}

// --- main entry: builds container + populates content ----------------------
export function renderSectionContent(
  attrs: SectionAttrs,
  content: string,
  { isOverlay, isSideSplit }: { isOverlay: boolean; isSideSplit: boolean }
): HTMLElement {
  const container = createSectionContainer(attrs);
  container.style.position = "relative";
  container.style.zIndex = "500";

  const { paddingV, paddingH } = resolvePadding(attrs, { isOverlay, isSideSplit });

  // ensure we have a .content element
  let contentEl = container.querySelector<HTMLElement>(".content");
  if (!contentEl) {
    contentEl = document.createElement("div");
    contentEl.className = "content";
    container.appendChild(contentEl);
  }

  // expose as CSS variables for downstream consumers (and GSAP if needed)
  // mirror on both content and container so frames/styles can read either scope
  const prio = String((attrs as any)["padding-important"] ?? "").toLowerCase().trim() === "true" ? "important" : "";
  contentEl.style.setProperty("--padding-v", paddingV);
  contentEl.style.setProperty("--padding-h", paddingH);
  container.style.setProperty("--padding-v", paddingV);
  container.style.setProperty("--padding-h", paddingH);

  // apply padding and sizing (logical props + CSS vars)
  contentEl.style.boxSizing = "border-box";
  // logical paddings via vars so svh/rem/vh behave predictably
  contentEl.style.setProperty("padding-block-start", "var(--padding-v)", prio);
  contentEl.style.setProperty("padding-block-end", "var(--padding-v)", prio);
  contentEl.style.setProperty("padding-inline-start", "var(--padding-h)", prio);
  contentEl.style.setProperty("padding-inline-end", "var(--padding-h)", prio);

  // width cap support (author can pass `content-width: 72ch` etc.)
  const contentWidth = (attrs as any)["content-width"];
  if (contentWidth) {
    contentEl.style.maxWidth = String(contentWidth);
    contentEl.style.marginInline = "auto";
  }
  contentEl.style.minWidth = "0"; // avoid overflow from long words

  // height cap: prefer svh (visible viewport), fall back to dvh/vh
  const contentMaxHeight = (attrs as any)["content-max-height"] as string | undefined;
  if (contentMaxHeight) {
    contentEl.style.setProperty("max-height", String(contentMaxHeight), prio);
  } else {
    contentEl.style.setProperty("max-height", `calc(100svh - (var(--padding-v) * 2))`);
    contentEl.style.setProperty("max-height", `calc(100dvh - (var(--padding-v) * 2))`);
    contentEl.style.setProperty("max-height", `calc(100vh  - (var(--padding-v) * 2))`);
  }

  // keep a data breadcrumb for debugging
  (contentEl.dataset as any).paddingV = paddingV;
  (contentEl.dataset as any).paddingH = paddingH;

  // render the markdown-derived lines into the container
  renderSectionContentLines(container, attrs, content);

  return container;
}